/**
 * Meetings Page - Find Recovery Meetings + My Meetings
 *
 * Two tabs:
 * - Find Meetings: access to official fellowship directories (unchanged)
 * - My Meetings: saved meetings, service commitments, attendance history
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  Cog6ToothIcon,
  MapPinIcon,
  ChevronDownIcon,
  PlusIcon,
  BookmarkIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from '../components/ui/Toast';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { useAuthStore } from '../stores/authStore';
import { getUserProfile, updateUserProfile } from '../services/firebase/userProfile';
import {
  getSavedMeetings,
  addSavedMeeting,
  updateSavedMeeting,
  deleteSavedMeeting,
  getAttendanceHistory,
  logAttendance,
  deleteAttendance,
  getServicePositions,
  addServicePosition,
  updateServicePosition,
  deactivateServicePosition,
} from '../services/firebase/meetings';
import type { CustomProgram } from '../stores/onboardingStore';
import {
  recoveryPrograms,
  programCategories,
  type ProgramCategory,
  type RecoveryProgram,
} from '../content/meeting-programs/programs';
import type { Fellowship } from '../types/meetings';
import type { SavedMeeting, MeetingAttendance, ServicePosition } from '../types/meetings';
import { MeetingCard } from '../components/features/meetings/MeetingCard';
import { MeetingForm } from '../components/features/meetings/MeetingForm';
import { LogAttendanceModal } from '../components/features/meetings/LogAttendanceModal';
import { ServicePositionForm } from '../components/features/meetings/ServicePositionForm';
import { analytics } from '../services/analytics';

// Category display order
const categoryOrder: ProgramCategory[] = ['substance', 'behavioral', 'family'];

/**
 * Append location to meeting finder URL if location is provided
 */
function appendLocationToUrl(baseUrl: string, location: string, programId: string): string {
  if (!location.trim()) {
    return baseUrl;
  }

  const encodedLocation = encodeURIComponent(location.trim());

  const urlPatterns: Record<string, (url: string, loc: string) => string> = {
    'aa': (url, loc) => `${url}?location=${loc}`,
    'na': (url, loc) => `${url}?location=${loc}`,
    'ca': (url, loc) => `${url}?location=${loc}`,
    'cma': (url, loc) => `${url}?location=${loc}`,
    'ha': (url, loc) => `${url}?location=${loc}`,
    'ma': (url, loc) => `${url}?location=${loc}`,
    'al-anon': (url, loc) => `${url}?location=${loc}`,
    'nar-anon': (url, loc) => `${url}?location=${loc}`,
    'ga': (url, loc) => `${url}?location=${loc}`,
    'oa': (url, loc) => `${url}?location=${loc}`,
    'sa': (url, loc) => `${url}?location=${loc}`,
    'saa': (url, loc) => `${url}?location=${loc}`,
    'sca': (url, loc) => `${url}?location=${loc}`,
    'slaa': (url, loc) => `${url}?location=${loc}`,
    'aca': (url, loc) => `${url}?location=${loc}`,
    'coda': (url, loc) => `${url}?location=${loc}`,
    'cosa': (url, loc) => `${url}?location=${loc}`,
    's-anon': (url, loc) => `${url}?location=${loc}`,
    'da': (url, loc) => `${url}?location=${loc}`,
  };

  const pattern = urlPatterns[programId];
  if (pattern) {
    return pattern(baseUrl, encodedLocation);
  }

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}location=${encodedLocation}`;
}

/**
 * Color mapping from hex colors to Tailwind classes
 */
const colorToTailwind = (hexColor: string): { bg: string; text: string } => {
  const colorMap: Record<string, { bg: string; text: string }> = {
    '#1e40af': { bg: 'bg-blue-700', text: 'text-blue-600 dark:text-blue-400' },
    '#7c3aed': { bg: 'bg-violet-600', text: 'text-violet-600 dark:text-violet-400' },
    '#0891b2': { bg: 'bg-cyan-600', text: 'text-cyan-600 dark:text-cyan-400' },
    '#4f46e5': { bg: 'bg-indigo-600', text: 'text-indigo-600 dark:text-indigo-400' },
    '#b45309': { bg: 'bg-amber-700', text: 'text-amber-600 dark:text-amber-400' },
    '#15803d': { bg: 'bg-green-700', text: 'text-green-600 dark:text-green-400' },
    '#166534': { bg: 'bg-green-700', text: 'text-green-600 dark:text-green-400' },
    '#dc2626': { bg: 'bg-red-600', text: 'text-red-600 dark:text-red-400' },
    '#059669': { bg: 'bg-emerald-600', text: 'text-emerald-600 dark:text-emerald-400' },
    '#d97706': { bg: 'bg-amber-600', text: 'text-amber-600 dark:text-amber-400' },
    '#ca8a04': { bg: 'bg-yellow-600', text: 'text-yellow-600 dark:text-yellow-400' },
    '#2563eb': { bg: 'bg-blue-600', text: 'text-blue-600 dark:text-blue-400' },
    '#ea580c': { bg: 'bg-orange-600', text: 'text-orange-600 dark:text-orange-400' },
    '#be185d': { bg: 'bg-pink-700', text: 'text-pink-600 dark:text-pink-400' },
    '#9333ea': { bg: 'bg-purple-600', text: 'text-purple-600 dark:text-purple-400' },
    '#c026d3': { bg: 'bg-fuchsia-600', text: 'text-fuchsia-600 dark:text-fuchsia-400' },
    '#e11d48': { bg: 'bg-rose-600', text: 'text-rose-600 dark:text-rose-400' },
    '#0d9488': { bg: 'bg-teal-600', text: 'text-teal-600 dark:text-teal-400' },
    '#475569': { bg: 'bg-slate-600', text: 'text-slate-600 dark:text-slate-400' },
    '#8b5cf6': { bg: 'bg-violet-500', text: 'text-violet-600 dark:text-violet-400' },
    '#0284c7': { bg: 'bg-sky-600', text: 'text-sky-600 dark:text-sky-400' },
    '#ec4899': { bg: 'bg-pink-500', text: 'text-pink-600 dark:text-pink-400' },
    '#f472b6': { bg: 'bg-pink-400', text: 'text-pink-600 dark:text-pink-400' },
    '#16a34a': { bg: 'bg-green-600', text: 'text-green-600 dark:text-green-400' },
    '#a855f7': { bg: 'bg-purple-500', text: 'text-purple-600 dark:text-purple-400' },
  };

  return colorMap[hexColor] || { bg: 'bg-primary-500', text: 'text-primary-600 dark:text-primary-400' };
};

const FELLOWSHIP_DIRECTORY: Record<string, Fellowship> = recoveryPrograms.reduce((acc, program) => {
  if (program.meetingFinderUrl) {
    const colors = colorToTailwind(program.color);
    acc[program.id] = {
      id: program.id,
      name: program.name,
      abbreviation: program.shortName,
      description: program.description,
      meetingFinderUrl: program.meetingFinderUrl,
      color: colors.bg,
      textColor: colors.text,
    };
  }
  return acc;
}, {} as Record<string, Fellowship>);

interface MeetingFinderCard {
  id: string;
  name: string;
  abbreviation: string;
  description: string;
  url: string | undefined;
  isCustom: boolean;
  color: string;
  textColor: string;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const },
  },
};

interface MeetingFinderCardProps {
  card: MeetingFinderCard;
  navigate: (path: string) => void;
  location: string;
}

function MeetingFinderCard({ card, navigate, location }: MeetingFinderCardProps) {
  const hasUrl = card.url !== undefined && card.url !== null;
  const isDisabled = !hasUrl;
  const mapsUrl = location.trim() && !card.isCustom
    ? `https://www.google.com/maps/search/${encodeURIComponent(card.name + ' meeting near ' + location)}`
    : null;

  return (
    <motion.div variants={itemVariants}>
      <Card
        variant="default"
        hoverable={hasUrl}
        className={`h-full transition-all duration-300 min-h-[44px] ${
          hasUrl ? 'hover:scale-[1.02]' : 'opacity-75'
        }`}
      >
        <CardContent className="!p-6 flex flex-col h-full">
          <div className="flex items-center gap-4 mb-4">
            <div
              className={`w-14 h-14 rounded-full ${
                isDisabled ? 'bg-surface-300 dark:bg-surface-600' : card.color
              } flex items-center justify-center flex-shrink-0`}
            >
              <span className={`font-bold text-xl ${
                isDisabled ? 'text-surface-500 dark:text-surface-400' : 'text-white'
              }`}>
                {card.abbreviation.slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 truncate">
                {card.name}
              </h3>
              <p className={`text-sm font-medium ${
                isDisabled ? 'text-surface-400 dark:text-surface-500' : card.textColor
              }`}>
                {card.abbreviation}
              </p>
              {card.isCustom && (
                <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-surface-200 dark:bg-surface-700 text-surface-600 dark:text-surface-400">
                  Custom Program
                </span>
              )}
            </div>
          </div>

          <p className="text-sm text-surface-600 dark:text-surface-400 mb-6 flex-1">
            {card.description}
          </p>

          {hasUrl ? (
            <div className="space-y-2">
              <a href={card.url} target="_blank" rel="noopener noreferrer" className="block">
                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  rightIcon={<ArrowTopRightOnSquareIcon className="w-4 h-4" />}
                  className="min-h-[44px] bg-navy-800 hover:bg-navy-900 dark:bg-teal-600 dark:hover:bg-teal-700"
                >
                  Find {card.abbreviation} Meetings
                </Button>
              </a>
              {mapsUrl && (
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="block">
                  <Button
                    variant="secondary"
                    size="sm"
                    fullWidth
                    rightIcon={<MapPinIcon className="w-4 h-4" />}
                    className="min-h-[44px]"
                  >
                    Search near {location}
                  </Button>
                </a>
              )}
            </div>
          ) : (
            <Button
              variant="secondary"
              size="md"
              fullWidth
              rightIcon={<Cog6ToothIcon className="w-4 h-4" />}
              className="min-h-[44px]"
              onClick={() => navigate('/settings')}
            >
              Update in Settings
            </Button>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function MeetingFinderDirectorySection({
  category,
  programs,
  isExpanded,
  onToggleExpand,
  userPrograms,
  onQuickAddProgram,
}: {
  category: ProgramCategory;
  programs: RecoveryProgram[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  userPrograms: string[];
  onQuickAddProgram: (programId: string, programName: string) => void;
}) {
  const categoryInfo = programCategories[category];

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between p-3 rounded-lg bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-750 transition-colors"
      >
        <div className="text-left">
          <h4 className="font-medium text-surface-800 dark:text-white text-sm">
            {categoryInfo.label}
          </h4>
          <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
            {programs.length} programs available
          </p>
        </div>
        <ChevronDownIcon
          className={`w-4 h-4 text-surface-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
            className="overflow-hidden"
          >
            <div className="space-y-2 pt-3">
              {programs.map((program) => (
                <div
                  key={program.id}
                  className="flex items-center justify-between gap-3 p-3 bg-surface-50 dark:bg-surface-900/50 rounded-lg"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-lg text-white font-bold text-xs flex-shrink-0"
                      style={{ backgroundColor: program.color }}
                    >
                      {program.shortName.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-surface-800 dark:text-white text-sm truncate">
                        {program.name}
                      </p>
                      <p className="text-xs text-surface-500 dark:text-surface-400">
                        {program.shortName}
                      </p>
                    </div>
                  </div>
                  {program.meetingFinderUrl ? (
                    <div className="flex gap-2">
                      <a
                        href={program.meetingFinderUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 text-white text-xs font-medium transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Find Meetings
                        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                      </a>
                      {!userPrograms.includes(program.id) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onQuickAddProgram(program.id, program.name);
                          }}
                          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-navy-800 hover:bg-navy-900 dark:bg-navy-700 dark:hover:bg-navy-600 text-white text-xs font-medium transition-colors"
                        >
                          <PlusIcon className="w-3.5 h-3.5" />
                          Add
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="flex-shrink-0 text-xs text-surface-400 dark:text-surface-500 italic">
                      No finder
                    </span>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function formatAttendanceDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatPositionStartDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function MeetingsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // ── Tab state ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'find' | 'mine'>('find');

  // ── Find Meetings state ──────────────────────────────────────────────────
  const [userPrograms, setUserPrograms] = useState<string[]>([]);
  const [userCustomPrograms, setUserCustomPrograms] = useState<CustomProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState<string>('');
  const [expandedCategories, setExpandedCategories] = useState<Set<ProgramCategory>>(new Set());
  const [addingProgram, setAddingProgram] = useState(false);
  const [locating, setLocating] = useState(false);

  // ── My Meetings state ────────────────────────────────────────────────────
  const [mineLoaded, setMineLoaded] = useState(false);
  const [mineLoading, setMineLoading] = useState(false);
  const [savedMeetings, setSavedMeetings] = useState<SavedMeeting[]>([]);
  const [servicePositions, setServicePositions] = useState<ServicePosition[]>([]);
  const [attendanceHistory, setAttendanceHistory] = useState<MeetingAttendance[]>([]);

  // Modal state
  const [meetingFormOpen, setMeetingFormOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<SavedMeeting | undefined>();
  const [logAttendanceTarget, setLogAttendanceTarget] = useState<SavedMeeting | null>(null);
  const [serviceFormOpen, setServiceFormOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<ServicePosition | undefined>();

  // ── Load programs (Find tab) ──────────────────────────────────────────────
  useEffect(() => {
    async function loadPrograms() {
      if (!user?.uid) { setLoading(false); return; }
      try {
        const profile = await getUserProfile(user.uid);
        if (profile) {
          setUserPrograms(profile.selectedPrograms || []);
          setUserCustomPrograms(profile.customPrograms || []);
        }
      } catch (error) {
        console.error('MeetingsPage - Error loading programs:', error);
      } finally {
        setLoading(false);
      }
    }
    loadPrograms();
  }, [user?.uid]);

  // ── Load location from localStorage ──────────────────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem('meeting-finder-location');
    if (saved) setLocation(saved);
  }, []);

  // ── Load My Meetings data (lazy — on first tab switch) ────────────────────
  const loadMineData = useCallback(async () => {
    if (!user?.uid || mineLoaded) return;
    setMineLoading(true);
    try {
      const [meetings, positions, history] = await Promise.all([
        getSavedMeetings(user.uid),
        getServicePositions(user.uid),
        getAttendanceHistory(user.uid, 30),
      ]);
      setSavedMeetings(meetings);
      setServicePositions(positions);
      setAttendanceHistory(history);
      setMineLoaded(true);
    } catch (error) {
      console.error('MeetingsPage - Error loading My Meetings:', error);
      toast.error('Load failed', 'Could not load your meetings. Please try again.');
    } finally {
      setMineLoading(false);
    }
  }, [user?.uid, mineLoaded]);

  useEffect(() => {
    if (activeTab === 'mine') {
      loadMineData();
    }
  }, [activeTab, loadMineData]);

  // ── Find Meetings handlers ────────────────────────────────────────────────
  const handleLocationChange = (value: string) => {
    setLocation(value);
    localStorage.setItem('meeting-finder-location', value);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Not supported', 'Your browser does not support GPS location.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { 'Accept-Language': navigator.languages?.length ? navigator.languages.join(',') : navigator.language || 'en' } }
          );
          const data = await res.json();
          const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county;
          const state = data.address?.state;
          if (city && state) {
            handleLocationChange(`${city}, ${state}`);
          } else if (city) {
            handleLocationChange(city);
          } else {
            toast.error('Location not found', 'Could not determine your city. Please enter it manually.');
          }
        } catch {
          toast.error('Location error', 'Could not retrieve your location. Please enter it manually.');
        } finally {
          setLocating(false);
        }
      },
      () => {
        toast.error('Location denied', 'Location access was denied. Please enter your location manually.');
        setLocating(false);
      },
      { timeout: 10000 }
    );
  };

  const toggleCategory = (category: ProgramCategory) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) newSet.delete(category);
      else newSet.add(category);
      return newSet;
    });
  };

  const handleQuickAddProgram = async (programId: string, programName: string) => {
    if (addingProgram || !user?.uid) return;
    setAddingProgram(true);
    try {
      const updatedPrograms = [...userPrograms, programId];
      await updateUserProfile(user.uid, { selectedPrograms: updatedPrograms });
      setUserPrograms(updatedPrograms);
      toast.success('Program added', `${programName} has been added to your programs.`);
    } catch (error) {
      console.error('Error adding program:', error);
      toast.error('Failed to add program', 'Please try again.');
    } finally {
      setAddingProgram(false);
    }
  };

  const programsByCategory = useMemo(() => {
    const grouped: Record<ProgramCategory, RecoveryProgram[]> = { substance: [], behavioral: [], family: [] };
    recoveryPrograms.forEach((program) => { grouped[program.category].push(program); });
    return grouped;
  }, []);

  const meetingFinderCards = useMemo(() => {
    const cards: MeetingFinderCard[] = [];
    if (!userPrograms || userPrograms.length === 0) return [];
    for (const programId of userPrograms) {
      const predefinedFellowship = FELLOWSHIP_DIRECTORY[programId];
      if (predefinedFellowship) {
        const baseUrl = predefinedFellowship.meetingFinderUrl;
        const urlWithLocation = baseUrl ? appendLocationToUrl(baseUrl, location, programId) : undefined;
        cards.push({
          id: predefinedFellowship.id,
          name: predefinedFellowship.name,
          abbreviation: predefinedFellowship.abbreviation,
          description: predefinedFellowship.description,
          url: urlWithLocation,
          isCustom: false,
          color: predefinedFellowship.color || 'bg-primary-500',
          textColor: predefinedFellowship.textColor || 'text-primary-600 dark:text-primary-400',
        });
      } else {
        const customProgram = userCustomPrograms.find((cp: CustomProgram) => cp.id === programId);
        if (customProgram) {
          cards.push({
            id: customProgram.id,
            name: customProgram.name,
            abbreviation: customProgram.abbreviation,
            description: customProgram.description || `A recovery program for ${customProgram.name} members`,
            url: customProgram.website,
            isCustom: true,
            color: 'bg-surface-400 dark:bg-surface-500',
            textColor: 'text-surface-600 dark:text-surface-400',
          });
        }
      }
    }
    return cards.sort((a, b) => {
      if (a.isCustom !== b.isCustom) return a.isCustom ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
  }, [userPrograms, userCustomPrograms, location]);

  const hasNoPrograms = meetingFinderCards.length === 0;

  // ── My Meetings handlers ──────────────────────────────────────────────────
  const handleSaveMeeting = async (data: Omit<SavedMeeting, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.uid) return;
    if (editingMeeting) {
      await updateSavedMeeting(user.uid, editingMeeting.id, data);
      setSavedMeetings(prev => prev.map(m => m.id === editingMeeting.id ? { ...m, ...data } : m));
      toast.success('Meeting updated', `"${data.name}" has been updated.`);
    } else {
      const id = await addSavedMeeting(user.uid, data);
      const now = { toDate: () => new Date(), seconds: Date.now() / 1000, nanoseconds: 0 };
      setSavedMeetings(prev => [...prev, { id, ...data, createdAt: now as never, updatedAt: now as never }]);
      analytics.meetingSaved(data.format, data.fellowship);
      toast.success('Meeting saved', `"${data.name}" added to your meetings.`);
    }
    setEditingMeeting(undefined);
  };

  const handleDeleteMeeting = async (meeting: SavedMeeting) => {
    if (!user?.uid) return;
    try {
      await deleteSavedMeeting(user.uid, meeting.id);
      setSavedMeetings(prev => prev.filter(m => m.id !== meeting.id));
      toast.success('Meeting removed', `"${meeting.name}" has been deleted.`);
    } catch {
      toast.error('Delete failed', 'Could not delete this meeting. Please try again.');
    }
  };

  const handleLogAttendance = async (data: {
    meetingId: string;
    meetingName: string;
    attendedDate: string;
    weeklyRole?: string;
    notes?: string;
  }) => {
    if (!user?.uid) return;
    const id = await logAttendance(user.uid, data);
    const now = { toDate: () => new Date(), seconds: Date.now() / 1000, nanoseconds: 0 };
    const newEntry: MeetingAttendance = { id, ...data, createdAt: now as never };
    setAttendanceHistory(prev => [newEntry, ...prev].slice(0, 30));
    analytics.attendanceLogged(!!data.weeklyRole);
    toast.success('Attendance logged', `Logged for ${data.meetingName}.`);
  };

  const handleDeleteAttendance = async (entry: MeetingAttendance) => {
    if (!user?.uid) return;
    try {
      await deleteAttendance(user.uid, entry.id);
      setAttendanceHistory(prev => prev.filter(e => e.id !== entry.id));
    } catch {
      toast.error('Delete failed', 'Could not remove this attendance entry.');
    }
  };

  const handleSavePosition = async (data: Omit<ServicePosition, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.uid) return;
    if (editingPosition) {
      await updateServicePosition(user.uid, editingPosition.id, data);
      setServicePositions(prev => prev.map(p => p.id === editingPosition.id ? { ...p, ...data } : p));
      toast.success('Position updated', `${data.positionName} updated.`);
    } else {
      const id = await addServicePosition(user.uid, data);
      const now = { toDate: () => new Date(), seconds: Date.now() / 1000, nanoseconds: 0 };
      setServicePositions(prev => [{ id, ...data, createdAt: now as never, updatedAt: now as never }, ...prev]);
      analytics.servicePositionAdded(data.positionName);
      toast.success('Position added', `${data.positionName} added to your service commitments.`);
    }
    setEditingPosition(undefined);
  };

  const handleDeactivatePosition = async (position: ServicePosition) => {
    if (!user?.uid) return;
    const endDate = new Date().toISOString().split('T')[0];
    try {
      await deactivateServicePosition(user.uid, position.id, endDate);
      setServicePositions(prev => prev.map(p => p.id === position.id ? { ...p, isActive: false, endDate } : p));
      toast.success('Position ended', `${position.positionName} marked as inactive.`);
    } catch {
      toast.error('Update failed', 'Could not deactivate this position.');
    }
  };

  const activePositions = servicePositions.filter(p => p.isActive);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900 pb-24 md:pb-8">
      {/* Header */}
      <header className="bg-white dark:bg-surface-800 border-b border-surface-200 dark:border-surface-700 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="md:hidden p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Go back"
            >
              <ArrowLeftIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-surface-900 dark:text-surface-100">
                Meetings
              </h1>
            </div>
          </div>

          {/* Tab bar */}
          <div className="flex gap-1 mt-3 p-1 bg-surface-100 dark:bg-surface-700 rounded-xl w-fit">
            {(['find', 'mine'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`
                  px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-200
                  ${activeTab === tab
                    ? 'bg-white dark:bg-surface-600 text-surface-900 dark:text-surface-100 shadow-sm'
                    : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-100'
                  }
                `}
              >
                {tab === 'find' ? 'Find Meetings' : 'My Meetings'}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">

          {/* ── Find Meetings Tab ── */}
          {activeTab === 'find' && (
            <motion.div
              key="find"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {loading && (
                <div className="flex justify-center items-center py-12">
                  <div className="text-surface-500 dark:text-surface-400">Loading your programs...</div>
                </div>
              )}

              {!loading && (
                <>
                  {!hasNoPrograms && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mb-6"
                    >
                      <Card variant="default">
                        <CardContent className="!p-5">
                          <label
                            htmlFor="location-input"
                            className="flex items-center gap-2 text-sm font-semibold text-surface-800 dark:text-surface-100 mb-3"
                          >
                            <MapPinIcon className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                            Your Location
                          </label>
                          <div className="flex gap-2">
                            <input
                              id="location-input"
                              type="text"
                              value={location}
                              onChange={(e) => handleLocationChange(e.target.value)}
                              placeholder="City, State or ZIP Code"
                              className="flex-1 px-4 py-3 rounded-lg border-2 border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-700 text-surface-800 dark:text-white placeholder:text-surface-400 dark:placeholder:text-surface-500 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                            />
                            <button
                              type="button"
                              onClick={handleUseMyLocation}
                              disabled={locating}
                              title="Use my current location"
                              className="flex-shrink-0 flex items-center gap-1.5 px-4 py-3 rounded-lg border-2 border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-700 hover:bg-surface-50 dark:hover:bg-surface-600 text-teal-600 dark:text-teal-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap text-sm font-medium"
                            >
                              <MapPinIcon className={`w-4 h-4 flex-shrink-0 ${locating ? 'animate-pulse' : ''}`} />
                              {locating ? 'Locating…' : 'Use my location'}
                            </button>
                          </div>
                          <p className="mt-2 text-xs text-surface-500 dark:text-surface-400">
                            Enter your city or use GPS to search for meetings near you.
                          </p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )}

                  {!hasNoPrograms && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 }}
                      className="mb-8"
                    >
                      <Card variant="filled" className="border-l-4 border-l-accent-500">
                        <CardContent className="!p-6">
                          <p className="text-surface-700 dark:text-surface-300 leading-relaxed">
                            Find recovery meetings near you through official fellowship directories.
                            Select a fellowship below to search for in-person, online, and hybrid meetings.
                          </p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )}

                  {hasNoPrograms ? (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Card variant="outlined" className="border-2">
                        <CardContent className="!p-12 text-center">
                          <div className="max-w-md mx-auto">
                            <div className="w-16 h-16 rounded-full bg-surface-200 dark:bg-surface-700 flex items-center justify-center mx-auto mb-4">
                              <ArrowTopRightOnSquareIcon className="w-8 h-8 text-surface-400 dark:text-surface-500" />
                            </div>
                            <h3 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-2">
                              No Programs Selected
                            </h3>
                            <p className="text-surface-600 dark:text-surface-400 mb-6">
                              Add recovery programs to your profile to access meeting finders for your fellowships.
                            </p>
                            <Button
                              variant="accent"
                              size="md"
                              onClick={() => navigate('/settings')}
                              rightIcon={<Cog6ToothIcon className="w-4 h-4" />}
                            >
                              Go to Settings
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ) : (
                    <>
                      <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
                      >
                        {meetingFinderCards.map((card) => (
                          <MeetingFinderCard key={card.id} card={card} navigate={navigate} location={location} />
                        ))}
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                        className="mt-8"
                      >
                        <div className="bg-accent-50 dark:bg-accent-900/10 rounded-xl p-5 border-2 border-accent-200 dark:border-accent-800">
                          <h3 className="font-semibold text-surface-800 dark:text-white mb-2 flex items-center gap-2">
                            <ArrowTopRightOnSquareIcon className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                            Meeting Finder Directory
                          </h3>
                          <p className="text-xs text-surface-600 dark:text-surface-400 mb-4">
                            Browse all available programs and access their official meeting finders, even programs you haven't selected.
                          </p>
                          {categoryOrder.map((category) => (
                            <MeetingFinderDirectorySection
                              key={`directory-${category}`}
                              category={category}
                              programs={programsByCategory[category]}
                              isExpanded={expandedCategories.has(category)}
                              onToggleExpand={() => toggleCategory(category)}
                              userPrograms={userPrograms}
                              onQuickAddProgram={handleQuickAddProgram}
                            />
                          ))}
                        </div>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.4 }}
                        className="mt-8"
                      >
                        <Card variant="outlined" className="border-dashed">
                          <CardContent className="!p-4">
                            <p className="text-xs text-surface-500 dark:text-surface-400 text-center">
                              All meeting finders link to official fellowship websites.
                              Meeting information is maintained by each fellowship independently.
                            </p>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </>
                  )}
                </>
              )}
            </motion.div>
          )}

          {/* ── My Meetings Tab ── */}
          {activeTab === 'mine' && (
            <motion.div
              key="mine"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {mineLoading && (
                <div className="flex justify-center items-center py-12">
                  <div className="text-surface-500 dark:text-surface-400">Loading your meetings...</div>
                </div>
              )}

              {!mineLoading && (
                <div className="space-y-8">

                  {/* ── Section 1: Saved Meetings ── */}
                  <section>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-lg font-semibold text-surface-900 dark:text-surface-100">
                          Saved Meetings
                        </h2>
                        <p className="text-sm text-surface-500 dark:text-surface-400">
                          Your personal meeting list
                        </p>
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<PlusIcon className="w-4 h-4" />}
                        onClick={() => { setEditingMeeting(undefined); setMeetingFormOpen(true); }}
                      >
                        Add Meeting
                      </Button>
                    </div>

                    {savedMeetings.length === 0 ? (
                      <EmptyState
                        variant="calendar"
                        title="No meetings saved yet"
                        description="Save your regular meetings to log attendance and track your service work."
                        action={
                          <Button
                            variant="primary"
                            size="sm"
                            leftIcon={<PlusIcon className="w-4 h-4" />}
                            onClick={() => { setEditingMeeting(undefined); setMeetingFormOpen(true); }}
                          >
                            Add Your First Meeting
                          </Button>
                        }
                      />
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {savedMeetings.map(meeting => (
                          <MeetingCard
                            key={meeting.id}
                            meeting={meeting}
                            onLogAttendance={m => setLogAttendanceTarget(m)}
                            onEdit={m => { setEditingMeeting(m); setMeetingFormOpen(true); }}
                            onDelete={handleDeleteMeeting}
                          />
                        ))}
                      </div>
                    )}
                  </section>

                  {/* ── Section 2: Service Commitments ── */}
                  <section>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-lg font-semibold text-surface-900 dark:text-surface-100 flex items-center gap-2">
                          <CheckCircleIcon className="w-5 h-5 text-teal-500" />
                          Service Commitments
                        </h2>
                        <p className="text-sm text-surface-500 dark:text-surface-400">
                          Step 12 — long-term service positions
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<PlusIcon className="w-4 h-4" />}
                        onClick={() => { setEditingPosition(undefined); setServiceFormOpen(true); }}
                      >
                        Add Position
                      </Button>
                    </div>

                    {activePositions.length === 0 && servicePositions.filter(p => !p.isActive).length === 0 ? (
                      <EmptyState
                        title="No service positions yet"
                        description="Track your long-term service commitments — Secretary, GSR, H&I, and more."
                        action={
                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<PlusIcon className="w-4 h-4" />}
                            onClick={() => { setEditingPosition(undefined); setServiceFormOpen(true); }}
                          >
                            Add a Position
                          </Button>
                        }
                      />
                    ) : (
                      <div className="space-y-2">
                        {/* Active positions */}
                        {activePositions.map(position => (
                          <Card key={position.id} variant="default" padding="sm">
                            <CardContent>
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-surface-900 dark:text-surface-100">
                                    {position.positionName}
                                  </p>
                                  {position.meetingName && (
                                    <p className="text-sm text-surface-500 dark:text-surface-400">
                                      {position.meetingName}
                                    </p>
                                  )}
                                  <p className="text-xs text-surface-400 dark:text-surface-500 mt-0.5">
                                    Since {formatPositionStartDate(position.startDate)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                                    Active
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => { setEditingPosition(position); setServiceFormOpen(true); }}
                                    className="p-1.5 rounded-lg text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors text-xs"
                                    title="Edit position"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeactivatePosition(position)}
                                    className="p-1.5 rounded-lg text-surface-400 hover:text-warning-600 dark:hover:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/20 transition-colors text-xs"
                                    title="Mark as ended"
                                  >
                                    End
                                  </button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}

                        {/* Inactive positions (collapsed) */}
                        {servicePositions.filter(p => !p.isActive).length > 0 && (
                          <p className="text-xs text-surface-400 dark:text-surface-500 pt-1">
                            + {servicePositions.filter(p => !p.isActive).length} past position(s)
                          </p>
                        )}
                      </div>
                    )}
                  </section>

                  {/* ── Section 3: Attendance History ── */}
                  <section>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-lg font-semibold text-surface-900 dark:text-surface-100 flex items-center gap-2">
                          <BookmarkIcon className="w-5 h-5 text-teal-500" />
                          Attendance History
                        </h2>
                        <p className="text-sm text-surface-500 dark:text-surface-400">
                          Last 30 entries
                        </p>
                      </div>
                    </div>

                    {attendanceHistory.length === 0 ? (
                      <EmptyState
                        title="No attendance logged yet"
                        description="Log attendance from a saved meeting card after each meeting you attend."
                      />
                    ) : (
                      <div className="space-y-2">
                        {attendanceHistory.map(entry => (
                          <div
                            key={entry.id}
                            className="flex items-start justify-between gap-3 p-3 bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm text-surface-900 dark:text-surface-100">
                                  {entry.meetingName}
                                </span>
                                {entry.weeklyRole && (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
                                    {entry.weeklyRole}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-surface-400 dark:text-surface-500 mt-0.5">
                                {formatAttendanceDate(entry.attendedDate)}
                              </p>
                              {entry.notes && (
                                <p className="text-xs text-surface-500 dark:text-surface-400 mt-1 truncate">
                                  {entry.notes}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteAttendance(entry)}
                              className="p-1.5 rounded-lg text-surface-300 hover:text-danger-500 dark:hover:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors flex-shrink-0"
                              aria-label="Delete attendance entry"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* ── Modals ── */}
      <MeetingForm
        isOpen={meetingFormOpen}
        onClose={() => { setMeetingFormOpen(false); setEditingMeeting(undefined); }}
        onSave={handleSaveMeeting}
        initialData={editingMeeting}
        selectedPrograms={userPrograms}
        customPrograms={userCustomPrograms}
      />

      {logAttendanceTarget && (
        <LogAttendanceModal
          isOpen={!!logAttendanceTarget}
          onClose={() => setLogAttendanceTarget(null)}
          meetingId={logAttendanceTarget.id}
          meetingName={logAttendanceTarget.name}
          onSaved={() => setLogAttendanceTarget(null)}
          onLogAttendance={handleLogAttendance}
        />
      )}

      <ServicePositionForm
        isOpen={serviceFormOpen}
        onClose={() => { setServiceFormOpen(false); setEditingPosition(undefined); }}
        onSave={handleSavePosition}
        initialData={editingPosition}
        savedMeetings={savedMeetings}
      />
    </div>
  );
}

export default MeetingsPage;
